import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest, map } from 'rxjs';
import { InhLanguage } from 'src/app/shared/constants-and-enums/language';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { ChangeLanguage } from 'src/app/store/actions/language.actions';
import { Logout } from 'src/app/store/actions/user.actions';
import { languages, selectedLanguage } from 'src/app/store/reducer/language.reducer';
import { isAllowed, isIotaAdmin, user } from 'src/app/store/reducer/user.reducer';
import { environment } from '../../../environments/environment';

interface SidebarMenuEntry {
    key: string;
    label?: string;
    heroicon?: string;
    flaticon?: string;
    route?: string;
    hidden?: boolean;
    expandedByDefault?: boolean;
    children?: SidebarMenuEntry[];
}

@Component({
    standalone: false,
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
    @Output() expandedChanged = new EventEmitter<boolean>();

    year = new Date().getFullYear();
    user$ = this.store.select(user);

    expanded = true;

    selectedLanguage$ = this.store.select(selectedLanguage);
    languages$ = this.store.select(languages);
    languagesDropdownOpen = false;

    expandedEntries: string[] = [];

    menuEntriesSubscription: Subscription;
    menuEntries$ = combineLatest([this.store.select(isAllowed), this.store.select(isIotaAdmin)]).pipe(
        map(([hasRequiredRole, isAdmin]): SidebarMenuEntry[] => {
            if (!hasRequiredRole) {
                return [
                    {
                        key: 'dashboard',
                        label: $localize`Dashboard`,
                        heroicon: 'squares-2x2',
                        route: '/dashboard'
                    }
                ];
            }

            return [
                {
                    key: 'dashboard',
                    label: $localize`Dashboard`,
                    heroicon: 'squares-2x2',
                    route: '/dashboard'
                },
                {
                    key: 'business-partner',
                    label: $localize`Business Partner`,
                    heroicon: 'briefcase',
                    expandedByDefault: true,
                    children: [
                        {
                            key: 'customers',
                            label: $localize`Customers`,
                            heroicon: 'building-office',
                            route: '/administration/customers'
                        },
                        {
                            key: 'solution-partners',
                            label: $localize`Solution Partners`,
                            heroicon: 'building-storefront',
                            route: '/administration/solution-partners'
                        },
                        {
                            key: 'central-regulators',
                            label: $localize`Central Regulators`,
                            heroicon: 'building-office-2',
                            route: '/administration/central-regulators'
                        },
                        {
                            key: 'health-insurance-funds',
                            label: $localize`Health Insurance Carriers`,
                            heroicon: 'shield-check',
                            route: '/administration/health-insurer'
                        }
                    ]
                },
                {
                    key: 'products-and-services',
                    label: $localize`Products & Services`,
                    heroicon: 'cube',
                    expandedByDefault: true,
                    children: [
                        {
                            key: 'products',
                            label: $localize`Products`,
                            heroicon: 'cube',
                            route: '/administration/product'
                        },
                        {
                            key: 'licenses',
                            label: $localize`Licenses`,
                            heroicon: 'cube-transparent',
                            route: '/administration/platform-license'
                        },
                        {
                            key: 'healthy-workplace',
                            label: $localize`Healthy Workplace`,
                            heroicon: 'face-smile',
                            route: '/administration/healthy-workplace'
                        }
                    ]
                },
                {
                    key: 'inh-billing',
                    label: $localize`INH Billing`,
                    flaticon: 'invoice-icon',
                    route: '/billing'
                },
                {
                    key: 'employees',
                    label: $localize`Employees`,
                    heroicon: 'users',
                    route: '/employees'
                },
                {
                    key: 'case-management',
                    label: $localize`Case Management`,
                    heroicon: 'document-duplicate',
                    expandedByDefault: true,
                    children: [
                        {
                            key: 'submissions',
                            label: $localize`Submissions`,
                            heroicon: 'document-arrow-down',
                            route: '/reimbursement/requests'
                        },
                        {
                            key: 'cases',
                            label: $localize`Cases`,
                            heroicon: 'document-check',
                            route: '/reimbursement/cases'
                        }
                    ]
                },
                {
                    key: 'reporting',
                    label: $localize`Reporting`,
                    heroicon: 'document-chart-bar',
                    expandedByDefault: true,
                    children: [
                        {
                            key: 'product-subscription-report',
                            label: $localize`Product Subscription Report`,
                            heroicon: 'document-chart-bar',
                            route: '/administration/product-subscription-report'
                        },
                        {
                            key: 'payroll-report',
                            label: $localize`Payroll Report`,
                            heroicon: 'document-chart-bar',
                            route: '/reporting/payroll-report'
                        },
                        {
                            key: 'product-selection-report',
                            label: $localize`Product Selection Report`,
                            heroicon: 'document-chart-bar',
                            route: '/reporting/product-selection-report'
                        }
                    ]
                },
                {
                    key: 'admin',
                    label: $localize`Admin`,
                    heroicon: 'lock-closed',
                    children: [
                        {
                            key: 'assets',
                            label: $localize`Media Assets`,
                            hidden: !isAdmin,
                            heroicon: 'photo',
                            route: '/media-assets'
                        },
                        {
                            key: 'email-signature',
                            label: $localize`Email Signature`,
                            heroicon: 'at-symbol',
                            route: '/admin/email-signature'
                        },
                        {
                            key: 'process-event-log',
                            label: $localize`Process Event Log`,
                            hidden: !isAdmin,
                            heroicon: 'clipboard-document-list',
                            route: '/admin/process-event-log'
                        },
                        {
                            key: 'version',
                            label: $localize`Version`,
                            heroicon: 'hashtag',
                            route: '/version-information'
                        }
                    ]
                }
            ];
        })
    );

    basePortalUrl$ = this.store
        .select(selectedLanguage)
        .pipe(map((language) => `${environment.employeePortalUrl}${language?.countryCode?.toLowerCase()}/metanavigation`));

    constructor(private store: Store) {}

    ngOnInit(): void {
        this.initializeMenu();
    }

    ngOnDestroy(): void {
        this.menuEntriesSubscription?.unsubscribe();
    }

    initializeMenu() {
        const expandedEntryKeys = LocalStorageService.getSidebarMainNavigationExpandedEntryKeys();

        // If the localStorage contains the expanded entries, only these entries are expanded for this user,
        // otherwise, the default expanded entries are used.
        if (!!expandedEntryKeys) {
            this.expandedEntries = expandedEntryKeys;
        } else {
            this.menuEntriesSubscription = this.menuEntries$.subscribe((menuEntries) => {
                menuEntries.forEach((entry) => {
                    if (entry?.expandedByDefault) {
                        this.expandedEntries.push(entry.key);
                    }
                });
            });
        }
    }

    toggleMenuEntryExpand(key: string): void {
        if (this.expandedEntries.includes(key)) {
            this.expandedEntries = this.expandedEntries.filter((entry) => entry !== key);
        } else {
            this.expandedEntries.push(key);
        }

        LocalStorageService.setSidebarMainNavigationExpandedEntryKeys(this.expandedEntries);
    }

    toggleLanguagesDropdown() {
        this.languagesDropdownOpen = !this.languagesDropdownOpen;
    }

    changeLanguage(language: InhLanguage): void {
        this.store.dispatch(ChangeLanguage({ language }));
    }

    doLogout(): void {
        this.store.dispatch(Logout());
    }

    protected readonly environment = environment;
}
